import { useState } from 'react'
import { useApp } from '../store/AppContext'

type View = 'signin' | 'signup' | 'reset'

export function AuthScreen() {
  const { signIn, signUp, resetPassword, authConfig, mfaPending, submitMfaCode, signOut } = useApp()
  const [view, setView] = useState<View>('signin')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [sentTo, setSentTo] = useState('')
  const [code, setCode] = useState('')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)

    if (view === 'reset') {
      const problem = await resetPassword(email)
      setBusy(false)

      /*
       * A fault is shown; an unknown address is not. GoTrue succeeds for an
       * address it does not know, so reaching here with a problem means the
       * mail genuinely failed -- and saying "on its way" in that case is the
       * one answer that helps nobody.
       */
      if (problem) {
        setErr(problem)
        return
      }

      setErr('')
      setSentTo(email.trim())
      return
    }

    const problem = view === 'signin' ? await signIn(email, password) : await signUp({ email, name, password })
    setBusy(false)
    if (problem) setErr(problem)
  }

  const swap = (next: View) => {
    setView(next)
    setErr('')
    setSentTo('')
  }

  if (mfaPending) {
    return (
      <div className="auth">
        <div className="auth-brandside">
          <div className="row" style={{ gap: 10 }}>
            <div className="brandmark brandmark--lg">C</div>
            <div style={{ fontWeight: 600, letterSpacing: '-.01em', fontSize: 16 }}>Cadence</div>
          </div>
          <div style={{ maxWidth: 420 }}>
            <div className="auth-eyebrow">One more step</div>
            <h1>Enter your code.</h1>
            <p>
              Open your authenticator app and type the six digits it shows for Cadence. They change
              every thirty seconds.
            </p>
          </div>
          <div />
        </div>

        <div className="auth-formside">
          <div className="auth-form">
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                if (busy) return
                setBusy(true)
                const problem = await submitMfaCode(code)
                setBusy(false)
                if (problem) setErr(problem)
                else setCode('')
              }}
            >
              <h2 style={{ fontSize: 20 }}>Two-factor code</h2>
              <p style={{ marginBottom: 16 }}>Six digits from your authenticator app.</p>

              <label className="label" htmlFor="au-code">
                Code
              </label>
              <input
                id="au-code"
                autoFocus
                className="input mono"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                style={{ marginBottom: 12, fontSize: 20, letterSpacing: '.2em', textAlign: 'center' }}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value)
                  setErr('')
                }}
                placeholder="000000"
              />

              {err && (
                <p style={{ color: 'var(--dg)', fontSize: 13, margin: '0 0 12px' }} role="alert">
                  {err}
                </p>
              )}

              <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
                {busy ? 'Checking…' : 'Verify'}
              </button>

              <div className="auth-swap">
                <button type="button" onClick={() => void signOut()}>
                  Cancel and sign out
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="auth">
      <div className="auth-brandside">
        <div className="row" style={{ gap: 10 }}>
          <div className="brandmark brandmark--lg">C</div>
          <div style={{ fontWeight: 600, letterSpacing: '-.01em', fontSize: 16 }}>Cadence</div>
        </div>
        <div style={{ maxWidth: 420 }}>
          <div className="auth-eyebrow">Time tracking for teams</div>
          <h1>Hours in, answers out.</h1>
          <p>
            Everyone tracks their own time with their own account. Owners and managers see the whole team's hours
            and cost, and export it in a click.
          </p>
        </div>
        <div className="auth-stats">
          <div>
            <b>Your account</b>
            <span>your own time</span>
          </div>
          <div>
            <b>One code</b>
            <span>to join a team</span>
          </div>
          <div>
            <b>CSV</b>
            <span>cost export</span>
          </div>
        </div>
      </div>

      <div className="auth-formside">
        <div className="auth-form">
          {/* Three views, so a two-way test left the reset screen wearing the
              sign-up heading: somebody asking for a new password was told they
              were creating an account. */}
          <h2>
            {view === 'signin'
              ? 'Sign in'
              : view === 'reset'
                ? 'Reset your password'
                : 'Create your account'}
          </h2>
          <p>
            {view === 'signin'
              ? 'Welcome back.'
              : view === 'reset'
                ? 'Enter the address you sign in with.'
                : 'One account works across every workspace you join.'}
          </p>

          {!authConfig.passwordLogin ? (
            <p style={{ fontSize: 13, color: 'var(--mut)', marginTop: 18 }} className="pretty">
              This server signs in with Google only.
            </p>
          ) : (
          <form onSubmit={submit}>
            {view === 'signup' && (
              <>
                <label className="label" htmlFor="au-name">
                  Your name
                </label>
                <input
                  id="au-name"
                  className="input"
                  autoComplete="name"
                  style={{ marginBottom: 16 }}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    setErr('')
                  }}
                  placeholder="Maya Ortiz"
                />
              </>
            )}

            <label className="label" htmlFor="au-email">
              Email
            </label>
            <input
              id="au-email"
              className="input"
              type="email"
              autoComplete="username"
              style={{ marginBottom: 16 }}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setErr('')
              }}
              placeholder="you@studio.com"
            />

            {view !== 'reset' && (
              <>
                <label className="label" htmlFor="au-pass">
                  Password
                </label>
                <input
                  id="au-pass"
                  className="input"
                  type="password"
                  autoComplete={view === 'signin' ? 'current-password' : 'new-password'}
                  style={{ marginBottom: 10 }}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    setErr('')
                  }}
                  placeholder={view === 'signin' ? '••••••••' : 'At least 8 characters'}
                />
              </>
            )}

            {view === 'reset' && (
              <p style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 12px' }}>
                {sentTo
                  ? `If ${sentTo} has an account, a link to set a new password is on its way. It expires in an hour. If nothing arrives and you signed up recently, an administrator has to approve you before you can sign in — ask them rather than trying again.`
                  : 'We’ll email you a link to set a new one.'}
              </p>
            )}

            {err && <div className="auth-err">{err}</div>}

            <button
              type="submit"
              className="btn btn--primary btn--block btn--lg"
              disabled={busy}
              style={{ marginTop: 10 }}
            >
              {busy
                ? 'One moment…'
                : view === 'signin'
                  ? 'Sign in'
                  : view === 'reset'
                    ? sentTo
                      ? 'Send again'
                      : 'Email me a link'
                    : 'Create account'}
            </button>
          </form>
          )}

          {authConfig.passwordLogin && (
          <div className="auth-swap">
            {view === 'signin' ? (
              <>
                <div style={{ marginBottom: 6 }}>
                  <button type="button" onClick={() => swap('reset')}>
                    Forgot your password?
                  </button>
                </div>
                New here?{' '}
                <button type="button" onClick={() => swap('signup')}>
                  Create an account
                </button>
              </>
            ) : view === 'reset' ? (
              <>
                Remembered it?{' '}
                <button type="button" onClick={() => swap('signin')}>
                  Sign in
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button type="button" onClick={() => swap('signin')}>
                  Sign in
                </button>
              </>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  )
}
